import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const embedSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().max(200).optional(),
  subtitle: z.string().max(400).optional(),
  provider: z.enum(['youtube', 'vimeo', 'loom', 'spotify', 'twitter', 'iframe']).default('youtube'),
  src: z.string().min(1).max(1000),
  aspect_ratio: z.enum(['16/9', '4/3', '1/1', '9/16']).default('16/9'),
  full_width: z.boolean().default(false).optional(),
});

export type EmbedData = z.infer<typeof embedSchema>;

const PROVIDER_TRANSFORMS: Record<string, (raw: string) => string> = {
  youtube: (raw) => {
    const m = raw.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
    return m ? `https://www.youtube-nocookie.com/embed/${m[1]}` : raw;
  },
  vimeo: (raw) => {
    const m = raw.match(/vimeo\.com\/(\d+)/);
    return m ? `https://player.vimeo.com/video/${m[1]}` : raw;
  },
  loom: (raw) => {
    const m = raw.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
    return m ? `https://www.loom.com/embed/${m[1]}` : raw;
  },
  spotify: (raw) => raw.replace('open.spotify.com/', 'open.spotify.com/embed/'),
  twitter: (raw) => raw,
  iframe: (raw) => raw,
};

export function EmbedRenderer({ eyebrow, h2, subtitle, provider = 'youtube', src, aspect_ratio = '16/9', full_width }: EmbedData) {
  const transformed = PROVIDER_TRANSFORMS[provider]?.(src) ?? src;
  return (
    <section className={`v5-section v5-embed-section${full_width ? ' is-full' : ''}`}>
      <div className={full_width ? '' : 'container'}>
        {(eyebrow || h2) && (
          <div className="v5-section-head">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            {h2 ? <h2 className="v5-h2-sm">{h2}</h2> : null}
            {subtitle ? <p className="v5-lead-sm">{subtitle}</p> : null}
          </div>
        )}
        <div className="v5-embed-wrap" style={{ aspectRatio: aspect_ratio.replace('/', ' / ') }}>
          <iframe
            src={transformed}
            title={h2 || 'Embed'}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="v5-embed-iframe"
            style={{ border: 0 }}
          />
        </div>
      </div>
    </section>
  );
}

export const embedBlock: BlockDefinition<EmbedData> = {
  meta: {
    type: 'embed',
    label: 'Embed (YouTube / Vimeo / etc)',
    description: 'Insertar vídeo o widget externo con aspect ratio fijo. Soporta YouTube, Vimeo, Loom, Spotify, iframes custom.',
    category: 'media',
    icon: 'play-circle',
  },
  schema: embedSchema,
  defaults: {
    provider: 'youtube',
    src: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    aspect_ratio: '16/9',
    full_width: false,
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 400 },
    {
      name: 'provider',
      label: 'Plataforma',
      type: 'select',
      options: [
        { label: 'YouTube', value: 'youtube' },
        { label: 'Vimeo', value: 'vimeo' },
        { label: 'Loom', value: 'loom' },
        { label: 'Spotify', value: 'spotify' },
        { label: 'Twitter/X', value: 'twitter' },
        { label: 'iframe custom', value: 'iframe' },
      ],
    },
    { name: 'src', label: 'URL del contenido', type: 'url', required: true, helperText: 'Pega la URL pública del recurso. Se transforma automáticamente en embed.' },
    {
      name: 'aspect_ratio',
      label: 'Aspect ratio',
      type: 'select',
      options: [
        { label: '16:9 (vídeo)', value: '16/9' },
        { label: '4:3', value: '4/3' },
        { label: '1:1 (cuadrado)', value: '1/1' },
        { label: '9:16 (vertical)', value: '9/16' },
      ],
    },
    { name: 'full_width', label: 'Ancho completo (full bleed)', type: 'boolean' },
  ],
  Renderer: EmbedRenderer,
};
